// src/pages/AddNewBusiness.jsx
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Modal, message } from 'antd'
import { CheckCircleTwoTone } from '@ant-design/icons'
import './AddNewBusiness.scss'
import Api from '../../Network/ApiConfig'

import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'
import { useHistory }  from "react-router-dom";

export default function AddNewBusiness() {
  const [form, setForm] = useState({
    FullName: '',
    fullPhoneNumber: '', // Will store E.164 formatted number
    Email: '',
    Password: '',
    ConfirmPassword: ''
  })
  const history = useHistory();
  const [passwordError, setPasswordError] = useState('')
  const [passwordInputError, setPasswordInputError] = useState([])
  const [showPassword, setShowPassword] = useState(false)

  const validatePassword = (password) => {
    const errors = [];
    const rules = [
      { message: 'Password must be more than 7 characters.', test: (pwd) => pwd.length > 7 },
      { message: 'Password must contain a lowercase letter.', test: (pwd) => /[a-z]/.test(pwd) },
      { message: 'Password must contain an uppercase letter.', test: (pwd) => /[A-Z]/.test(pwd) },
      { message: 'Password must contain a digit.', test: (pwd) => /\d/.test(pwd) },
      { message: 'Password must contain a special character.', test: (pwd) => /[^a-zA-Z0-9]/.test(pwd) }
    ];

    rules.forEach(rule => {
      if (!rule.test(password)) {
        errors.push(rule.message);
      }
    });
    return errors;
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)

  const handleModalOk = () => {
    setIsModalVisible(false)
    setForm({ FullName: '', fullPhoneNumber: '', Email: '', Password: '', ConfirmPassword: '' })
  }

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({
      ...f,
      [name]: value,
    }));

    if (name === 'Password') {
      setPasswordInputError(validatePassword(value));
      if (form.ConfirmPassword) { // Clear mismatch error if confirm field has content
          setPasswordError(''); 
      }
    } else if (name === 'ConfirmPassword') {
        setPasswordError(''); // Clear mismatch error when confirm password changes
    }
  };

  const handleSubmit = async e => {
    e.preventDefault()

    const passwordValidationErrors = validatePassword(form.Password);
    if (passwordValidationErrors.length > 0) {
      setPasswordInputError(passwordValidationErrors);
      return;
    }
    setPasswordInputError([]); // Clear specific password error if validation passes

    // Validate passwords match
    if (form.Password !== form.ConfirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    setPasswordError('');

    setIsLoading(true)
    try {
      const phoneNumber = form.fullPhoneNumber ? parsePhoneNumberFromString(form.fullPhoneNumber) : null;
      const apiPayload = {
        FullName: form.FullName,
        Email: form.Email,
        PhoneNumber: phoneNumber ? phoneNumber.nationalNumber : '',
        PhoneKey: phoneNumber ? `+${phoneNumber.countryCallingCode}` : '',
        Password: form.Password,
        ConfirmPassword: form.ConfirmPassword // As per API spec
      };

      const response = await Api.post('Business/AddBusiness', apiPayload)
     
      
      if (response.data) { // Assuming response.data indicates success
        setIsSuccess(true)
        setIsModalVisible(true)
        setForm({ FullName: '', fullPhoneNumber: '', Email: '', Password: '', ConfirmPassword: '' })
        message.success('Business added successfully!')
        history.push({
          pathname: "/add-order",
        });
      }
      else{
         message.error(response?.message || 'Failed to add business');
      }
      
     
    } catch (error) {
      console.error('Error adding business:', error)
      // Handle status code 400 specifically
      if (error?.response?.status === 400) {
        const apiMsg = error?.response?.data?.errors?.message;
        if (apiMsg) {
          message.error(apiMsg);
        } else {
          message.error('Bad request. Please check your input.');
        }
      } else {
        // Show error message from API using antd message.error
        const apiMsg = error?.response?.data?.errors?.message;
        if (apiMsg) {
          message.error(apiMsg);
        } else {
          message.error('Failed to add business. Please try again.');
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="add-new-business-page">
      <div className="form-container">
        <h2>Add New Business</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="text"
              name="FullName"
              placeholder="Business Name"
              value={form.FullName}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <input
              type="email"
              name="Email"
              placeholder="Business Email"
              value={form.Email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <PhoneInput
              international
              defaultCountry="AE"
              value={form.fullPhoneNumber}
              onChange={(value) => setForm(prev => ({ ...prev, fullPhoneNumber: value }))}
              className="phone-input"
              required
              displayInitialValue
            />
          </div>

          <div className="form-group password-group">
            <input
              type={showPassword ? 'text' : 'password'}
              name="Password"
              placeholder="Password"
              value={form.Password}
              onChange={handleChange}
              required
            />
            <span 
              className="toggle-password" 
              onClick={togglePasswordVisibility}
              role="button"
              tabIndex="0"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? '👁️' : '👁️'}
            </span>
          </div>
          {passwordInputError.length > 0 && (
            <div className="error-message-list" style={{ textAlign: 'left', fontSize: '0.8em', color: 'red', marginTop: '-10px', marginBottom: '10px', paddingLeft: '10px' }}>
              {passwordInputError.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </div>
          )}

          <div className="form-group password-group">
            <input
              type={showPassword ? 'text' : 'password'}
              name="ConfirmPassword"
              placeholder="Confirm Password"
              value={form.ConfirmPassword}
              onChange={handleChange}
              required
            />
            <span 
              className="toggle-password" 
              onClick={togglePasswordVisibility}
              role="button"
              tabIndex="0"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? '👁️' : '👁️'}
            </span>
          </div>

          {passwordError && (
            <div className="error-message">
              {passwordError}
            </div>
          )}

          <button
            type="submit"
            className="submit-button"
            disabled={isLoading}
          >
            {isLoading ? 'Adding Business...' : 'Add New Business'}
          </button>

          {isSuccess && (
            <Modal
              title="Success"
              open={isModalVisible}
              onOk={handleModalOk}
              okText="OK"
              style={{ textAlign: 'center' }}
              footer={[
                <button key="ok" onClick={handleModalOk} className="ant-btn ant-btn-primary">
                  OK
                </button>
              ]}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '1rem', flexDirection: 'column' }}>
                <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: '5rem' }} />
                <span>Business added successfully!</span>
              </div>
            </Modal>
          )}
        </form>
      </div>
    </div>
  )
}
